import { FunctionComponent, useEffect, useState } from "react"
import { deleteVendor, editVendor, fetchVendor, fetchVendors, findVendorOrderNumber, increaseOrderInList, reduceOrderInList, Vendor, VendorApiPayload } from "../vendorsSlice"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { useAuth } from "react-oidc-context"
import { isFulfilled } from "@reduxjs/toolkit"
import { useNavigate } from "react-router"

import styled from 'styled-components'

export const Wrapper = styled.article`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border-radius: 8px;
  background: white;
  box-shadow: 0 1px 4px rgba(64,64,64,0.15);

  .vendor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .vendor-name {
    font-size: 1.25rem;
    font-weight: bold;
    margin: 0;
  }

  .vendor-category {
    font-size: 0.8rem;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    background: var(--color-pink, #e91e8c);
    color: white;
    white-space: nowrap;
  }

  .vendor-description {
    margin-top: 0.5rem;
  }

  .vendor-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .vendor-controls {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
  }
`

type VendorCardParams = {
  key: string
  vendor: Vendor,
}

export const VendorCard: FunctionComponent<VendorCardParams> = ({ vendor }) => {
  const dispatch = useAppDispatch();
  const vendorsstate = useAppSelector(state => state.vendorsstate);
  const auth = useAuth();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([] as Vendor[])

  useEffect(() => {
    setVendors(vendorsstate.vendors.slice().sort((a, b) => a.vendorOrder - b.vendorOrder));
  }, [vendorsstate.vendors])

  const handleSwap = async (up: Vendor, down: Vendor) => {
    const at: string = auth.user?.access_token as string;
    const action1 = await dispatch(editVendor({ accessToken: at, id: up.id, vendor: up }));
    const action2 = await dispatch(editVendor({ accessToken: at, id: down.id, vendor: down }));
    if (isFulfilled(action1) && isFulfilled(action2)) {
      dispatch(fetchVendors());
    }
  }

  const handleUp = async () => {
    const actionOrder = findVendorOrderNumber(vendors, vendor.id);
    if (actionOrder !== 0) {
      const upDn = reduceOrderInList(vendors, vendor);
      handleSwap(upDn.lower, upDn.rise);
    }
  }

  const handleDown = async () => {
    const actionOrder = findVendorOrderNumber(vendors, vendor.id);
    if (actionOrder !== vendors.length - 1) {
      const upDn = increaseOrderInList(vendors, vendor);
      handleSwap(upDn.lower, upDn.rise);
    }
  }

  const handleEdit = async () => {
    const action = await dispatch(fetchVendor({ id: vendor.id } as VendorApiPayload));
    if (isFulfilled(action)) {
      navigate(`/vendor-edit/${vendor.id}`);
    }
  }

  const handleDelete = async () => {
    const actionOrder = findVendorOrderNumber(vendors, vendor.id);
    vendors.forEach(v => {
      if (v.vendorOrder > actionOrder) {
        v.vendorOrder = v.vendorOrder - 1;
        const payload: VendorApiPayload = { accessToken: auth.user?.access_token as string, id: v.id, vendor: v };
        dispatch(editVendor(payload));
      }
    })

    const action = await dispatch(deleteVendor({ accessToken: auth.user?.access_token as string, id: vendor.id } as VendorApiPayload));
    if (isFulfilled(action)) {
      navigate('/vendors');
    }
  }

  return (
    <Wrapper>
      {vendor.imageUrl && (
        <img src={vendor.imageUrl} alt={vendor.name} style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px', marginBottom: '0.5rem' }} />
      )}
      <div className="vendor-header">
        <h3 className="vendor-name">{vendor.name}</h3>
        {vendor.category && <span className="vendor-category">{vendor.category}</span>}
      </div>

      {vendor.description && (
        <div className="vendor-description" dangerouslySetInnerHTML={{ __html: vendor.description }} />
      )}

      <div className="vendor-footer">
        {vendor.website && (
          <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="btn btn-go">
            Visit Website
          </a>
        )}

        {auth.isAuthenticated && (
          <div className="vendor-controls">
            <button type='button' className='btn' onClick={() => handleUp()}>Up</button>
            <button type='button' className='btn' onClick={() => handleDown()}>Down</button>
            <button type='button' className='btn' onClick={() => handleEdit()}>Edit</button>
            <button type='button' className='btn btn-danger' onClick={() => handleDelete()}>Delete</button>
          </div>
        )}
      </div>
    </Wrapper>
  )
}
